import React, { createRef, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './ContentMenu.module.less';
import tabsStyles from './Tabs.module.less';
import clsx from 'clsx';
import AppLink from '../../links/AppLink';
import { SvgIcon } from 'edcom-ui';
import {
  MOBILE_NAV_CONTENT_MENU_BACK_BUTTON_ID,
  tabInfoPropType,
} from '../util';
import { trackGA } from 'services/track';

export default function ContentMenu({ tabInfo, backToMenu, hideMenu }) {
  const [activeSubmenuIndex, setActiveSubmenuIndex] = useState(null);
  const sectionsWithRefs = useRef({});

  // height diff is the maximum height of the expanded sub menu for the current screen size
  const [heightDiff, setHeightDiff] = useState(0);

  const outerRef = useRef(null);
  const headerRef = useRef(null);
  const browseAllRef = useRef(null);
  const buttonRef = useRef(null);

  const SUBMENU_BOTTOM_PADDING = 30;
  const BROWSEALL_BOTTOM_PADDING = 30;

  // 1. Trigger the initial animation (sliding down the first section) when the component loads
  // 2. Trigger the initial calculation of the maximum sub menu expansion height when the component loads
  useEffect(() => {
    setActiveSubmenuIndex(null);
    const difference = getSubMenuHeight();
    setHeightDiff(difference > 0 ? difference : 0);
  }, []);

  // Trigger a rerender if the height of the current active Submenu has changed, so we update its height
  useEffect(() => {
    const resizeSubmenu = () => {
      if (activeSubmenuIndex !== null) {
        const sectionRef =
          sectionsWithRefs.current[activeSubmenuIndex].ref.current;
        if (outerRef.current && buttonRef.current && browseAllRef.current) {
          const difference = getSubMenuHeight();
          setHeightDiff(difference > 0 ? difference : 0); // Ensure the height difference is non-negative
        }

        if (
          sectionRef.prevHeight &&
          sectionRef.prevHeight !== sectionRef.offsetHeight
        ) {
          // Force a re-render (ugly but works)
          setActiveSubmenuIndex(null);
          setActiveSubmenuIndex(activeSubmenuIndex);
        }

        // Whenever we resize the window, save the current height of the ref as prevHeight so that, if it changes, we
        // can trigger the re-render above
        sectionRef.prevHeight = sectionRef.offsetHeight;
      }
    };

    // Since the sections can change height because we update the font-size for mobile widths, we need to trigger a
    // rerender if the height of the submenu changed. To do this, add an event listener on resize and an ugly function
    window.addEventListener('resize', resizeSubmenu);
    return () => window.removeEventListener('resize', resizeSubmenu);
  }, [activeSubmenuIndex]);

  const { name, sections, urlName } = tabInfo;

  // Only add the Browse All button for subjects and grades in mobile
  const hasBrowseAll = name === 'Subjects' || name === 'Grades';


  /**
   * A way to calculate the height of the expanded submenu where we subtract the number of menu buttons (items) times the height of
   * each button (item), the height of browseAll section, and the extra padding from the total height since we want to make sure
   * all the buttons could display on a single page (with correct padding) without scrolling
   * @returns {number}
   */
  const getSubMenuHeight = () => {
    const outerHeight = outerRef.current.offsetHeight;
    return (
      outerHeight -
      buttonRef.current.offsetHeight * sectionsWithRefs.current.length -
      (browseAllRef.current ? browseAllRef.current.offsetHeight : 0) -
      SUBMENU_BOTTOM_PADDING -
      BROWSEALL_BOTTOM_PADDING
    );
  };

  /**
   * Builds an array for the different grades of this content in the shape of the sections elements. This way, we can
   * build them together. Also, add a "ref" property to each section, so we can... well, reference it, so we can keep
   * track of the sections heights (see comments in the useEffect above)
   * @returns [{items, title, ref}]
   */
  const getSectionsWithRefs = () => {
    let allSections = sections[0].items;
    return allSections.map(section => ({ ...section, ref: createRef() }));
  };

  const expandSubMenu = (isOpen, sectionIndex) => {
    setActiveSubmenuIndex(isOpen ? null : sectionIndex);

    // Get the specific "gradient" container
    const gradient = document.getElementById(`container-${sectionIndex}`);
    const sectionRef = sectionsWithRefs.current[sectionIndex].ref.current;
    const scrollHeight = sectionRef.scrollHeight;

    // Calculate the absolute position of the gradient for the expanded sub menu if we can't display all the secondary items without scrolling
    // display the gradient
    if (scrollHeight - SUBMENU_BOTTOM_PADDING > heightDiff) {
      gradient.style.display = 'block';
      const topPosition =
        (buttonRef.current.offsetHeight + 1) * (sectionIndex + 1) +
        headerRef.current.offsetHeight +
        heightDiff;
      gradient.style.top = `${topPosition}px`;
    } else {
      gradient.style.display = 'none';
    }
  };

  const handleClickTrackingMobile = (sectionName, text, url) => {
    const gtmObject = {
      event: 'click_button',
      feature: 'Navigation',
      section: `${name} Sub-Menu`,
      button: `${sectionName} Links`,
      click_text: text,
      click_url: `https://${window.location.hostname}${url}`,
    };
    trackGA('Sub Menu', 'click_button', '', '', gtmObject);
  };

  // Initialize the sectionsWithRefs ref (this will happen only once)
  if (!sectionsWithRefs.current.length) {
    sectionsWithRefs.current = getSectionsWithRefs();
  }

  return (
    <div className={styles.contentMenu}>
      <div className={styles.displayHeader} ref={headerRef}>
        <div className={styles.header}>
          <button
            id={MOBILE_NAV_CONTENT_MENU_BACK_BUTTON_ID}
            className={clsx(styles.backIconContainer, styles.emptyButton)}
            type="button"
            aria-label="Back"
            onClick={backToMenu}
          >
            <SvgIcon name="arrowBack" aria-hidden />
          </button>
          <h3 className={styles.title}>{name}</h3>
        </div>
      </div>

      <div className={styles.sectionsContainer} ref={outerRef}>
        {sectionsWithRefs.current.map((section, sectionIndex) => {
          const isOpen = activeSubmenuIndex === sectionIndex;
          const { title, url, secondary_items } = section;

          return (
            <div
              key={title}
              className={clsx(styles.section, isOpen && styles.open)}
            >
              {secondary_items === undefined || secondary_items.length === 0 ? (
                <AppLink href={url} onClick={hideMenu}>
                  <button
                    className={clsx(styles.sectionHeader, styles.emptyButton)}
                    aria-label={title}
                    type="button"
                    ref={buttonRef}
                  >
                    <h4 className={styles.sectionTitle}>{title}</h4>
                  </button>
                </AppLink>
              ) : (
                <button
                  className={clsx(styles.sectionHeader, styles.emptyButton)}
                  aria-label={`${isOpen ? 'Close' : 'Expand'} ${title}`}
                  aria-expanded={isOpen}
                  type="button"
                  onClick={() => expandSubMenu(isOpen, sectionIndex)}
                  ref={buttonRef}
                >
                  <h4 className={styles.sectionTitle}>{title}</h4>
                  {<SvgIcon name={isOpen ? 'minus' : 'plus'} aria-hidden />}
                </button>
              )}
              <div
                className={styles.sectionItemsContainer}
                aria-hidden={!isOpen}
                ref={el => {
                  if (el) {
                    if (isOpen) {
                      // activate scroll bar if needed when opening a sub menu
                      const currentHeight = el.scrollHeight;
                      el.style.overflowY =
                        currentHeight - SUBMENU_BOTTOM_PADDING > heightDiff
                          ? 'scroll'
                          : 'hidden';
                      el.style.height =
                        currentHeight - SUBMENU_BOTTOM_PADDING > heightDiff
                          ? `${heightDiff}px`
                          : `${currentHeight}px`;
                      el.style.paddingBottom = `${SUBMENU_BOTTOM_PADDING}px`;
                    } else {
                      el.style.height = null; // Reset height when closed
                      el.style.paddingBottom = `0`;
                    }
                  }
                }}
              >
                {secondary_items !== undefined && (
                  <ul className={styles.sectionItems} ref={section.ref}>
                    <li key={''} className={clsx(styles.mainItem)}>
                      <AppLink href={url} onClick={hideMenu}>
                        <span className={styles.fontViewAll}>View All</span>
                      </AppLink>
                    </li>
                    {secondary_items.map(item => (
                      <li
                        key={item.url}
                        className={clsx(
                          item.type === 'Main Item' && styles.mainItem,
                          styles.itemGap
                        )}
                      >
                        <AppLink
                          href={item.url}
                          onClick={() =>
                            handleClickTrackingMobile(
                              title,
                              item.title,
                              item.url
                            )
                          }
                        >
                          {item.title}
                        </AppLink>
                      </li>
                    ))}
                  </ul>
                )}
                <div
                  className={styles.seeMoreContainer}
                  id={`container-${sectionIndex}`}
                >
                  <div className={styles.gradient} id="gradient"></div>
                </div>
              </div>
            </div>
          );
        })}
        {hasBrowseAll && (
          <div ref={browseAllRef}>
            <AppLink
              href={urlName}
              className={tabsStyles.browseAll}
              onClick={hideMenu}
            >
              <span>Browse All {name}</span>
              <SvgIcon
                name="arrowRight"
                className={tabsStyles.arrowRight}
                aria-hidden
              />
            </AppLink>
          </div>
        )}
      </div>
    </div>
  );
}

ContentMenu.propTypes = {
  tabInfo: tabInfoPropType.isRequired,
  backToMenu: PropTypes.func.isRequired,
  hideMenu: PropTypes.func.isRequired,
};
